import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, CheckCircle2, Info, Zap, BookOpen } from 'lucide-react';
import { LatexRenderer } from '../../ui/LatexRenderer';
import { Card } from '../../ui/Card';
interface ExplanationCardProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
  solutionSteps?: string[];
}
// Renders explanation as numbered steps or plain text
const ExplanationSteps = ({
  text,
  steps,
  accent = 'indigo'
}: {
  text?: string;
  steps?: string[];
  accent?: 'indigo' | 'amber';
}) => {
  const stepsToRender = steps || (text ? text.split('\n').filter(line => line.trim().length > 0) : []);
  if (stepsToRender.length <= 1 && text) {
    return <div className="text-lg leading-relaxed text-slate-700" data-id="element-4351">
        <LatexRenderer content={text} data-id="element-4352" />
      </div>;
  }
  const stepBg = accent === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-indigo-50 text-indigo-600 border-indigo-100';
  return <div className="space-y-4" data-id="element-4353">
      {stepsToRender.map((step, idx) => <motion.div key={idx} initial={{
      opacity: 0,
      x: -8
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.05 * idx,
      duration: 0.3
    }} className="flex gap-4 items-start" data-id="element-4354">
          <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm border shadow-sm mt-0.5 ${stepBg}`} data-id="element-4355">
            {idx + 1}
          </div>
          <div className="flex-1 text-lg leading-relaxed pt-0.5 text-slate-700" data-id="element-4356">
            <LatexRenderer content={step} data-id="element-4357" />
          </div>
        </motion.div>)}
    </div>;
};
/**
 * ExplanationCard — friendly teacher note aesthetic.
 * Correct: warm amber/yellow background, 'Hoe je dit oplost' with Lightbulb
 * Wrong: soft rose distractor explanation + amber teacher note
 */
export function ExplanationCard({
  explanation,
  distractorExplanation,
  isCorrect,
  mode = 'full',
  solutionSteps
}: ExplanationCardProps) {
  if (mode === 'compact') {
    return <div className="bg-amber-50 p-4 rounded-xl border border-amber-200" data-id="element-4358">
        <div className="flex items-center gap-2 font-bold text-amber-800 mb-2 text-sm" data-id="element-4359">
          <Zap className="w-4 h-4" data-id="element-4360" />
          Uitleg
        </div>
        <div className="text-amber-900 text-sm leading-relaxed" data-id="element-4361">
          <LatexRenderer content={explanation} data-id="element-4362" />
        </div>
      </div>;
  }
  return <div className="space-y-5" data-id="element-4363">
      {/* WRONG ANSWER — why was it wrong? */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-4364">
          <Card className="border-2 border-rose-100 bg-white overflow-hidden shadow-sm rounded-[2rem]" data-id="element-4365">
            <div className="bg-rose-50 px-6 py-4 border-b border-rose-100 flex items-center gap-3" data-id="element-4366">
              <div className="bg-white p-2 rounded-xl shadow-sm border border-rose-100 text-rose-500" data-id="element-4367">
                <Info className="w-5 h-5" data-id="element-4368" />
              </div>
              <h3 className="text-lg font-black text-rose-900" data-id="element-4369">
                Waarom was dat niet goed?
              </h3>
            </div>
            <div className="p-7 bg-rose-50/20" data-id="element-4370">
              <ExplanationSteps text={distractorExplanation} accent="indigo" data-id="element-4371" />
            </div>
          </Card>
        </motion.div>}

      {/* TEACHER NOTE — warm amber/yellow, friendly tone */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: !isCorrect && distractorExplanation ? 0.1 : 0,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-4372">
        {/* Teacher note card — amber/yellow warm background */}
        <div className="relative rounded-[2rem] overflow-hidden border-2 border-amber-200 shadow-md" data-id="element-4373">
          {/* Decorative top stripe */}
          <div className="h-1.5 w-full bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400" data-id="element-4374" />

          {/* Header */}
          <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center gap-3" data-id="element-4375">
            <div className="bg-white p-2 rounded-xl shadow-sm border border-amber-200" data-id="element-4376">
              {isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-4377" /> : <Lightbulb className="w-5 h-5 text-amber-500 fill-amber-100" data-id="element-4378" />}
            </div>
            <div className="flex-1" data-id="element-4379">
              <h3 className="text-lg font-black text-amber-900" data-id="element-4380">
                {isCorrect ? 'Waarom dit klopt ✓' : 'Zo los je dit op'}
              </h3>
              <p className="text-xs text-amber-600 font-medium mt-0.5 flex items-center gap-1" data-id="element-4381">
                <BookOpen className="w-3 h-3" data-id="element-4382" />
                Uitleg van de juf/meester
              </p>
            </div>
          </div>

          {/* Body — warm amber background, teacher note feel */}
          <div className="bg-amber-50/60 p-7" data-id="element-4383">
            {/* Subtle lined paper effect */}
            <div className="relative" style={{
            backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, #fde68a40 31px, #fde68a40 32px)',
            backgroundSize: '100% 32px'
          }} data-id="element-4384">
              <ExplanationSteps text={explanation || 'Goed gedaan! Je hebt de juiste strategie gebruikt.'} steps={solutionSteps} accent="amber" data-id="element-4385" />
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}